import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import PersonMessages from "../components/PersonMessages";
import { Users, Plus, Pencil, Trash2, Link2, Mail, UserRound, Bell, MessagesSquare, Send, MailCheck, Phone, MessageCircle, MoreVertical } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { toast } from "sonner";

const RELATIONS = ["Figlio/a", "Genitore", "Coniuge/Partner", "Fratello/Sorella", "Nonno/a", "Nipote", "Amico/a", "Altro"];
const emptyForm = { name: "", email: "", phone: "", relationship: "", welcome_message: "", heir_id: null };

const Persone = () => {
  const [people, setPeople] = useState(null);
  const [reportUrl, setReportUrl] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [allMemories, setAllMemories] = useState([]);
  const [msgPerson, setMsgPerson] = useState(null);
  const [msgStartNew, setMsgStartNew] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteFor, setInviteFor] = useState(null);
  const [inviteText, setInviteText] = useState("");

  const load = () => api.get("/people").then((res) => setPeople(res.data)).catch(() => setPeople([]));
  const loadMemories = () => api.get("/memories").then((r) => setAllMemories(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadMemories();
    api.get("/death-report-link").then((r) => setReportUrl(r.data.url)).catch(() => {});
  }, []);

  const countFor = (p) => allMemories.filter((m) => (m.heir_ids || []).includes(p.heir_id)).length;

  const openMessages = (p) => { setMsgStartNew(false); setMsgPerson(p); };
  const openNewMessage = (p) => { setMsgStartNew(true); setMsgPerson(p); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openAdd = () => { setForm(emptyForm); setEditing(false); setOpen(true); };
  const openEdit = (p) => {
    setForm({ name: p.name || "", email: p.email || "", phone: p.phone || "", relationship: p.relationship || "", welcome_message: p.welcome_message || "", heir_id: p.heir_id });
    setEditing(true); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome"); return; }
    setSaving(true);
    try {
      await api.post("/people", { name: form.name, email: form.email || null, phone: form.phone || null, relationship: form.relationship || null, welcome_message: form.welcome_message, heir_id: form.heir_id, is_heir: true });
      toast.success("Legame salvato");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    try { await api.post("/people/delete", { heir_id: p.heir_id }); toast.success("Legame rimosso"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nella rimozione"); }
  };

  const phoneMessage = (p) => {
    // SMS/WhatsApp delivery requires a messaging provider (Twilio) not yet configured.
    toast.info("Invio via SMS/WhatsApp in arrivo: attiveremo questa funzione appena colleghi un servizio di messaggistica.");
  };

  const copy = (text, msg) => { navigator.clipboard.writeText(text); toast.success(msg); };

  const openInvite = (p) => {
    if (!p.email) { toast.error("Aggiungi prima un'email a questa persona"); return; }
    setInviteFor(p);
    setInviteText(p.invite_message || "");
  };
  const sendInvite = async () => {
    setInviting(true);
    try {
      await api.post(`/people/${inviteFor.heir_id}/invite`, { message: inviteText });
      toast.success(`Email inviata a ${inviteFor.name}`);
      setInviteFor(null); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nell'invio");
    } finally { setInviting(false); }
  };
  const dialogTitle = editing ? "Modifica legame" : "Nuovo legame";

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-10 fade-in">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#FBEDE7] flex items-center justify-center"><Users className="w-6 h-6 text-primary" /></div>
            <div>
              <h1 className="font-serif text-4xl tracking-tight">I tuoi legami</h1>
              <p className="text-muted-foreground mt-1 max-w-xl">Le persone care con cui crei un legame, a cui lasci messaggi dedicati da rilasciare alla tua scomparsa o a una data che scegli tu.</p>
            </div>
          </div>
          <Button onClick={openAdd} className="rounded-full bg-primary hover:bg-[#b0553d] text-white h-11 px-6" data-testid="persone-add-btn"><Plus className="w-4 h-4 mr-2" /> Aggiungi legame</Button>
        </div>

        <div className="bg-white rounded-2xl border border-border p-6 mb-6" data-testid="death-report-card">
          <div className="flex items-center gap-2 mb-2"><Bell className="w-5 h-5 text-primary" /><p className="font-serif text-lg">Link per comunicare la scomparsa</p></div>
          <p className="text-sm text-muted-foreground mb-4">Condividi questo link, quando sei in vita, con eredi e familiari di fiducia. Sarà tramite questo (o dal portale erede) che chi ne ha titolo potrà comunicare la tua scomparsa caricando il certificato di morte, dando il via al rilascio dei contenuti secondo le tue regole.</p>
          <div className="flex items-center gap-2">
            <Input readOnly value={reportUrl} className="bg-[#F4F1EA] border-border rounded-xl h-11 text-sm" data-testid="death-report-url" />
            <Button onClick={() => copy(reportUrl, "Link copiato")} variant="outline" className="rounded-full h-11 px-5 shrink-0" data-testid="death-report-copy"><Link2 className="w-4 h-4 mr-2" /> Copia</Button>
          </div>
        </div>

        {people === null ? (
          <p className="text-muted-foreground">Caricamento…</p>
        ) : people.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-border" data-testid="persone-empty">
            <UserRound className="w-8 h-8 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nessun legame ancora. Crea il primo legame per iniziare.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {people.map((p, i) => (
              <div key={p.heir_id || i} className="bg-white rounded-2xl border border-border p-4 flex items-center gap-4" data-testid={`person-card-${i}`}>
                <div className="w-10 h-10 rounded-full bg-[#F4F1EA] flex items-center justify-center shrink-0"><UserRound className="w-5 h-5 text-[#c9a892]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <h3 className="font-serif text-lg leading-tight">{p.name}</h3>
                    {p.relationship && <span className="text-xs text-muted-foreground">{p.relationship}</span>}
                  </div>
                  <div className="flex items-center gap-x-4 gap-y-0.5 flex-wrap text-xs text-muted-foreground mt-0.5">
                    {p.email && <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {p.email}</span>}
                    {p.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {p.phone}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <button onClick={() => openNewMessage(p)} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-medium" data-testid={`person-new-message-${i}`}>
                      <Plus className="w-3.5 h-3.5" /> Nuovo messaggio
                    </button>
                    <button onClick={() => openMessages(p)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid={`person-messages-${i}`}>
                      <MessagesSquare className="w-3.5 h-3.5" /> Messaggi ({countFor(p)})
                    </button>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-2 rounded-full hover:bg-[#F4F1EA] transition-colors shrink-0" data-testid={`person-menu-${i}`}><MoreVertical className="w-4 h-4 text-muted-foreground" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white" data-testid={`person-menu-content-${i}`}>
                    <DropdownMenuItem onClick={() => openInvite(p)} data-testid={`person-invite-${i}`}>
                      {p.invited_at ? <MailCheck className="w-4 h-4 mr-2 text-[#4e8f6f]" /> : <Send className="w-4 h-4 mr-2" />}
                      {p.invited_at ? "Avvisa di nuovo" : "Avvisa come erede"}
                    </DropdownMenuItem>
                    {p.phone && (
                      <DropdownMenuItem onClick={() => phoneMessage(p)} data-testid={`person-phone-message-${i}`}>
                        <MessageCircle className="w-4 h-4 mr-2 text-[#4e8f6f]" /> Messaggio SMS/WhatsApp
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => copy(`${window.location.origin}/erede/${p.heir_token}`, "Link erede copiato")} data-testid={`person-copy-heir-${i}`}>
                      <Link2 className="w-4 h-4 mr-2" /> Copia link erede
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => openEdit(p)} data-testid={`person-edit-${i}`}>
                      <Pencil className="w-4 h-4 mr-2" /> Modifica
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => remove(p)} className="text-destructive focus:text-destructive" data-testid={`person-delete-${i}`}>
                      <Trash2 className="w-4 h-4 mr-2" /> Elimina
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyForm); setEditing(false); } }}>
          <DialogContent className="bg-[#F9F6F0] rounded-2xl max-w-md" data-testid="person-dialog">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{dialogTitle}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="mb-1.5 block">Nome</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Nome e cognome" className="bg-white border-border rounded-xl h-11" data-testid="person-name-input" /></div>
              <div><Label className="mb-1.5 block">Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="email@esempio.it" className="bg-white border-border rounded-xl h-11" data-testid="person-email-input" /></div>
              <div><Label className="mb-1.5 block">Numero di telefono (facoltativo)</Label><Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+39 333 1234567" className="bg-white border-border rounded-xl h-11" data-testid="person-phone-input" /></div>
              <div>
                <Label className="mb-1.5 block">Relazione</Label>
                <Select value={form.relationship} onValueChange={(v) => set("relationship", v)}>
                  <SelectTrigger className="bg-white border-border rounded-xl h-11" data-testid="person-relationship-select"><SelectValue placeholder="Scegli…" /></SelectTrigger>
                  <SelectContent className="bg-white">{RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block">Messaggio di benvenuto (facoltativo)</Label><Textarea value={form.welcome_message} onChange={(e) => set("welcome_message", e.target.value)} rows={3} placeholder="Un messaggio personale per questa persona…" className="bg-white border-border rounded-xl resize-none" data-testid="person-welcome-input" /></div>
              <Button onClick={save} disabled={saving} className="w-full rounded-full bg-primary hover:bg-[#b0553d] text-white h-11" data-testid="person-save-btn">{saving ? "Attendere…" : "Salva"}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {msgPerson && (
          <PersonMessages
            person={msgPerson}
            open={!!msgPerson}
            startNew={msgStartNew}
            onOpenChange={(v) => { if (!v) { setMsgPerson(null); setMsgStartNew(false); loadMemories(); } }}
            onChanged={loadMemories}
          />
        )}

        <Dialog open={!!inviteFor} onOpenChange={(v) => { if (!v) setInviteFor(null); }}>
          <DialogContent className="bg-[#F9F6F0] rounded-2xl max-w-md" data-testid="invite-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Avvisa {inviteFor?.name} come erede</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground -mt-1">Invieremo un'email che spiega il suo ruolo e cosa fare al momento della scomparsa. Puoi aggiungere due righe tue, personali, che compariranno in cima al messaggio.</p>
            <Textarea value={inviteText} onChange={(e) => setInviteText(e.target.value)} rows={4} maxLength={400}
              placeholder="Es. Ciao Marco, ti ho scelto perché mi fido di te. Grazie di cuore." className="bg-white border-border rounded-xl resize-none mt-2" data-testid="invite-message-input" />
            <div className="flex items-center gap-3 mt-3">
              <Button onClick={sendInvite} disabled={inviting} className="flex-1 rounded-full bg-primary hover:bg-[#b0553d] text-white h-11" data-testid="invite-send-btn">
                {inviting ? "Invio…" : "Invia invito"}
              </Button>
              <Button variant="ghost" onClick={() => setInviteFor(null)} className="rounded-full h-11" data-testid="invite-cancel-btn">Annulla</Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default Persone;
